/*
 * Copyright (c) 2025. Dave van Rijn Development
 */

import {NumberString} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';

export type FieldDefinition = {
    expectedType: ModelFieldType;
    convertToBoolean: boolean;
    allowNull?: boolean;
    allowUndefined?: boolean;
    defaultValue?: any
};

export enum ModelFieldType {STRING, NUMBER, BOOLEAN, OBJECT, ARRAY, BIGINT, NUMBER_STRING}

export const createFieldDef = (expectedType: ModelFieldType, defaultValue?: any, allowNull: boolean = false,
                               allowUndefined: boolean = false,
                               convertToBoolean: boolean = false): FieldDefinition => ({
    expectedType,
    allowUndefined,
    allowNull,
    convertToBoolean,
    defaultValue
});

export interface BaseProps {
    id?: string;
    created_at?: string;
    updated_at?: string;
}

export interface BaseFields {
    id: string;
    created_at: string;
    updated_at: string;
}

export abstract class BaseModel<Props extends BaseProps, Fields extends BaseFields,
    FieldDefType extends string | number | symbol = keyof Fields> extends Object {
    private readonly fieldFilter: (keyof Fields)[];
    protected readonly fields: Fields;
    protected readonly props: Props;
    private readonly FieldsDefinition: Record<FieldDefType, FieldDefinition>;
    private readonly tempFields: Record<string, any>;
    protected abstract build: (data?: Props) => void;

    protected constructor(fieldFilter: Array<keyof Fields>, fieldsDefinition: Record<FieldDefType, FieldDefinition>, fields: Fields,
                          props: Props | undefined) {
        super(props);
        fields = {...fields};
        this.fields = fields;
        this.props = props || {} as Props;
        this.fieldFilter = fieldFilter;
        this.FieldsDefinition = fieldsDefinition;
        this.tempFields = {};
    }

    setField = (key: keyof Fields, value: any) => {
        if (this.fields.hasOwnProperty(key))
            this.fields[key] = value;
        return this;
    };

    getField = (key: keyof Fields): any | null => {
        if (this.fields.hasOwnProperty(key))
            return this.fields[key];
        return null;
    };

    setTempField = (key: string, value: any) => {
        this.tempFields[key] = value;
    };

    getTempField = (key: string): any | null => {
        if (key in this.tempFields) return this.tempFields[key];
        return null;
    };

    get id(): string {
        return this.fields.id;
    }

    set id(id: string) {
        this.fields.id = id;
    }

    get createdAt(): string {
        return this.fields.created_at;
    }

    set createdAt(created_at: string) {
        this.fields.created_at = created_at;
    }

    get createdAtMoment(): IDate {
        return new IDate(this.createdAt);
    }

    set createdAtMoment(value: IDate) {
        this.fields.created_at = value.format();
    }

    get updatedAt(): string {
        return this.fields.updated_at;
    }

    set updatedAt(updated_at: string) {
        this.fields.updated_at = updated_at;
    }

    get updatedAtMoment(): IDate {
        return new IDate(this.updatedAt);
    }

    protected constructFields = (data = this.props) => {
        const preparedData: Partial<Fields> = this.prepareData(data);
        const fieldKeys: Array<keyof Fields> = Object.keys(this.fields) as Array<keyof Fields>;
        const keys = fieldKeys.filter(key => !this.fieldFilter.includes(key));
        for (const key of keys) this.initializeField(key, preparedData[key]);
    };

    private initializeField = (key: keyof Fields, value: any) => {
        if (this.fields.hasOwnProperty(key) && value !== undefined)
            this.fields[key] = value;
    };

    private prepareData = (data: Props): Partial<Fields> => {
        const prepareKeys = Object.keys(this.FieldsDefinition) as Array<keyof Fields>;
        const preparedData: Partial<Record<keyof Fields, any>> = {};
        for (const key of prepareKeys) {
            const definition: FieldDefinition = this.FieldsDefinition[key as FieldDefType],
                {allowNull, allowUndefined, defaultValue} = definition;
            let value = data[key as keyof Props];
            if ((value === null && !allowNull) || (value === undefined && !allowUndefined)) {
                if (defaultValue !== undefined) preparedData[key] = defaultValue;
                else preparedData[key] = preparedData[key] = this.determineDefaultValue(definition);
            } else if ((value === null && allowNull) || (value === undefined && (allowUndefined || allowNull))) {
                preparedData[key] = null;
            } else
                preparedData[key] = this.validateExistingValue(value, definition);
        }
        return preparedData;
    };

    private determineDefaultValue = (definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition;
        const typeToUse = convertToBoolean ? ModelFieldType.BOOLEAN : expectedType;
        switch (typeToUse) {
            case ModelFieldType.STRING:
                return '';
            case ModelFieldType.NUMBER:
                return 0;
            case ModelFieldType.NUMBER_STRING:
                return new NumberString(0);
            case ModelFieldType.BIGINT:
                return 0n;
            case ModelFieldType.BOOLEAN:
                return false;
            case ModelFieldType.OBJECT:
                return {};
            case ModelFieldType.ARRAY:
                return new Array<any>();
        }
        return null;
    };

    private validateExistingValue = (value: any, definition: FieldDefinition): any => {
        const {expectedType, convertToBoolean} = definition;
        if (convertToBoolean && (value === true || value === false)) return value;
        switch (expectedType) {
            case ModelFieldType.STRING:
                if (typeof value !== 'string') value = value.toString();
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.NUMBER:
                if (typeof value !== 'number') value = Number(value);
                if (Number.isNaN(value)) value = 0;
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.NUMBER_STRING:
                return new NumberString(value);
            case ModelFieldType.BIGINT:
                return BigInt(value);
            case ModelFieldType.BOOLEAN:
                if (value !== true && value !== false) value = false;
                return value;
            case ModelFieldType.OBJECT:
                try {
                    Object.keys(value).length;
                } catch {
                    value = {};
                }
                return this.convertBoolean(value, expectedType, convertToBoolean);
            case ModelFieldType.ARRAY:
                if (!Array.isArray(value)) value = [];
                return this.convertBoolean(value, expectedType, convertToBoolean);
            default:
                return null;
        }
    };

    private convertBoolean = (value: any, expectedType: ModelFieldType, shouldConvert: boolean = false): any => {
        if (!shouldConvert) return value;
        switch (expectedType) {
            case ModelFieldType.STRING:
                return value === 'true';
            case ModelFieldType.NUMBER:
                return value === 1;
            case ModelFieldType.NUMBER_STRING:
                return (value as NumberString).numberValue === 1;
            case ModelFieldType.BOOLEAN:
                return value;
            case ModelFieldType.OBJECT:
                try {
                    return Object.keys(value).length > 0;
                } catch {
                    return false;
                }
            case ModelFieldType.ARRAY:
                try {
                    return value.length > 0;
                } catch {
                    return false;
                }
        }
        return false;
    };
}